import { Brain, Factory, Flame, Gauge, Target, Users, Wrench, Zap } from 'lucide-react';
import { Tool } from '../types';
import { FLAMECO_CATEGORY_HUES } from '../flameco-ui';

// One entry per tool: drives the dashboard tiles, the side nav and the
// User Management module list. `name` is the nav label and the page title.
export const TOOLS: Tool[] = [
  {
    id: 'mto-configurator', hue: 55,
    tag: 'mto-configurator.view',
    name: 'MTO configurator',
    description: 'Make to order costing for welded parts from process, material and wall thickness factors.',
    icon: Wrench,
    path: '/mto-configurator'
  },
  {
    id: 'flame-cell-calculator', hue: 25,
    tag: 'flame-cell-calculator.view',
    name: 'Flame cell calculator',
    description: 'Flame cell element maximum flow, pressure drop and head loss.',
    icon: Gauge,
    path: '/flame-cell-calculator'
  },
  {
    id: 'production-planning', hue: 260,
    tag: 'production-planning.view',
    name: 'Production planning',
    description: 'Weekly production schedule, welder assignments and capacity planning.',
    icon: Factory,
    path: '/production-planning'
  },
  {
    id: 'threaded-inline-arrestors', hue: 15,
    tag: 'flame-arrestors.view',
    name: 'Flame arrestors',
    description: 'Sizing, pressure drop performance and part number reference for Threaded Inline and Flanged Deflagration arrestors.',
    icon: Flame,
    path: '/flame-arrestors'
  },
  {
    id: 'laser-quote', hue: 300,
    tag: 'laser-quote.view',
    name: 'Laser quote',
    description: 'Cost-stack quoting for the 12 kW fiber laser: machine rate, job costing, materials and margin pricing.',
    icon: Zap,
    path: '/laser-quote'
  },
  {
    id: 'tribal-knowledge', hue: 175,
    tag: 'tribal-knowledge.view',
    name: 'Tribal knowledge',
    description: 'Shared manufacturing know-how, lessons learned and reference docs.',
    icon: Brain,
    path: '/tribal-knowledge'
  },
  {
    id: 'balanced-strategic-plan', hue: 150,
    tag: 'balanced-strategic-plan.view',
    name: 'Balanced strategic plan',
    description: 'Executive scorecard: vision and mission, strategic themes, strategy map and decision backlog.',
    icon: Target,
    path: '/balanced-strategic-plan'
  },
  {
    id: 'user-management', hue: 230,
    tag: 'user-management.view',
    name: 'Users and roles',
    description: 'Role assignments and per-role module access. Admins only.',
    icon: Users,
    path: '/user-management'
  }
];

/** The tool registered at `id` (the page header reads its name and description). */
export const toolById = (id: string): Tool => {
  const t = TOOLS.find(x => x.id === id);
  if (!t) throw new Error(`Unknown tool ${id}`);
  return t;
};

/**
 * How the tools are grouped on the home page and in the side nav, each group
 * in its category colour (FLAMECO_CATEGORY_HUES, the same in every Flameco app).
 */
export interface ToolCategory {
  key: string;
  label: string;
  hue: number | null;
  toolIds: string[];
}

export const TOOL_CATEGORIES: ToolCategory[] = [
  {
    key: 'engineering', label: 'Engineering and quoting', hue: FLAMECO_CATEGORY_HUES.engineering,
    toolIds: ['mto-configurator', 'flame-cell-calculator', 'threaded-inline-arrestors', 'laser-quote'],
  },
  { key: 'planning', label: 'Planning', hue: FLAMECO_CATEGORY_HUES.planning, toolIds: ['production-planning', 'balanced-strategic-plan'] },
  { key: 'knowledge', label: 'Knowledge', hue: FLAMECO_CATEGORY_HUES.knowledge, toolIds: ['tribal-knowledge'] },
  { key: 'admin', label: 'Administration', hue: FLAMECO_CATEGORY_HUES.admin, toolIds: ['user-management'] },
];

/** The visible tools grouped by category (empty categories dropped; any tool not listed goes last). */
export function groupTools(tools: Tool[]): { category: ToolCategory; tools: Tool[] }[] {
  const listed = new Set(TOOL_CATEGORIES.flatMap((c) => c.toolIds));
  const groups = TOOL_CATEGORIES.map((category) => ({
    category,
    tools: category.toolIds.map((id) => tools.find((t) => t.id === id)).filter((t): t is Tool => !!t),
  }));
  const rest = tools.filter((t) => !listed.has(t.id));
  if (rest.length) groups.push({ category: { key: 'other', label: 'More tools', hue: FLAMECO_CATEGORY_HUES.tools, toolIds: [] }, tools: rest });
  return groups.filter((g) => g.tools.length > 0);
}
